import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  signIn,
  signUp,
  verifyEmail,
  forgotPassword,
  resetPassword,
  signOut as signOutApi,
  setPassword,
  getProviders,
  unlinkProvider,
} from '@/api/auth';
import { getMyProfile } from '@/api/profiles';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  ACCESS_TOKEN_EXPIRES_AT_KEY,
  REFRESH_TOKEN_EXPIRES_AT_KEY,
  EMAIL_UNVERIFIED_ADDRESS,
  MY_PROFILE_QUERY_KEY,
} from '@/constants';
import { trackUmamiEvent } from '@/helpers/analytics';
import { getAttributionSource } from '@/helpers/attribution';
import { notify } from '@/services/notifications';
import { UserDto } from '@/types/auth';

export type AuthUser = UserDto;

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  emailVerified: boolean;
  isLoginPendingEmailVerification: boolean;
  unverifiedEmail: string | null;
  // actions
  setUser: (user: AuthUser | null) => void;
  setLoading: (loading: boolean) => void;
  setUnverifiedEmail: (email: string | null) => void;
  clearTokens: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      loading: false,
      emailVerified: false,
      isLoginPendingEmailVerification: false, // Now derived from user state, not localStorage
      unverifiedEmail: null,

      setUser: (user) => {
        set({ user, isAuthenticated: !!user, emailVerified: !!user?.emailVerified });
      },

      setLoading: (loading) => set({ loading }),

      setUnverifiedEmail: (email) => {
        set({ unverifiedEmail: email });
        // Sync with localStorage for persistence across page reloads
        if (email) {
          localStorage.setItem(EMAIL_UNVERIFIED_ADDRESS, email);
        } else {
          localStorage.removeItem(EMAIL_UNVERIFIED_ADDRESS);
        }
      },

      clearTokens: () => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
        localStorage.removeItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        emailVerified: state.emailVerified,
        isLoginPendingEmailVerification: state.isLoginPendingEmailVerification,
        unverifiedEmail: state.unverifiedEmail,
      }),
    },
  ),
);

// React Query hooks - use MY_PROFILE_QUERY_KEY to share cache with useMyProfile (avoids duplicate /me calls)
export const AUTH_QUERY_KEYS = {
  me: MY_PROFILE_QUERY_KEY, // Alias for invalidation - same as profile/me
};

function profileToUser(profile: { user_id: string; username: string; avatar_url?: string | null; email?: string | null; email_verified?: boolean | null }) {
  return {
    id: profile.user_id,
    username: profile.username,
    avatar_url: profile.avatar_url,
    email: profile.email || '',
    emailVerified: profile.email_verified ?? true,
  };
}

export function useMe() {
  const setUser = useAuth((s) => s.setUser);
  const setLoading = useAuth((s) => s.setLoading);
  const hasAccessToken = typeof window !== 'undefined' && !!localStorage.getItem(ACCESS_TOKEN_KEY);
  const query = useQuery({
    queryKey: MY_PROFILE_QUERY_KEY,
    queryFn: async () => {
      const res = await getMyProfile();
      return res.data;
    },
    select: (profile) => profileToUser(profile),
    enabled: hasAccessToken,
    retry: (failureCount, error: unknown) => {
      const e = error as { status?: number } | undefined;
      if (e?.status === 401) return false;
      return failureCount < 2;
    },
  });

  // Sync Zustand state
  React.useEffect(() => {
    if (query.data) setUser(query.data);
  }, [query.data, setUser]);
  React.useEffect(() => {
    if (!query.isError) return;
    const status = (query.error as { status?: number } | undefined)?.status;
    // Only clear session on auth failure — Zod/network errors must not log the user out.
    if (status === 401) {
      setUser(null);
    }
  }, [query.isError, query.error, setUser]);
  React.useEffect(() => {
    if (!query.isFetching) setLoading(false);
  }, [query.isFetching, setLoading]);

  return query;
}

export function useSignIn() {
  const setUser = useAuth((s) => s.setUser);
  const setUnverifiedEmail = useAuth((s) => s.setUnverifiedEmail);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: signIn,
    onSuccess: (res) => {
      const { user, tokens } = res.data;
      if (tokens.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      if (tokens.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      if (tokens.accessTokenExpires) localStorage.setItem(ACCESS_TOKEN_EXPIRES_AT_KEY, tokens.accessTokenExpires);
      if (tokens.refreshTokenExpires) localStorage.setItem(REFRESH_TOKEN_EXPIRES_AT_KEY, tokens.refreshTokenExpires);

      // Clear email address from store (successful login)
      setUnverifiedEmail(null);

      setUser(user);
      notify.success('Signed in');
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
      // Invalidate duels to refetch with user_vote
      queryClient.invalidateQueries({ queryKey: ['duels'] });
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; code?: string; message?: string };
      if (e.code === 'INVALID_CREDENTIALS' || e.status === 401) {
        return notify.error('Invalid credentials');
      }
      if (e.code === 'EMAIL_NOT_VERIFIED') {
        return notify.info('Email not verified. Check your inbox.');
      }
      notify.error(e.message || 'Sign-in error');
    },
  });
}

export function useSignUp() {
  const queryClient = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  const setUnverifiedEmail = useAuth((s) => s.setUnverifiedEmail);
  return useMutation({
    mutationFn: signUp,
    onSuccess: (res) => {
      // Save tokens so auth works (email may be unverified)
      const { user, tokens } = res.data;
      if (tokens.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      if (tokens.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      if (tokens.accessTokenExpires) localStorage.setItem(ACCESS_TOKEN_EXPIRES_AT_KEY, tokens.accessTokenExpires);
      if (tokens.refreshTokenExpires) localStorage.setItem(REFRESH_TOKEN_EXPIRES_AT_KEY, tokens.refreshTokenExpires);

      // Store email address for resend hint (user IS logged in but unverified)
      // Using Zustand store for reactivity - banner will show immediately
      if (!user.emailVerified) {
        setUnverifiedEmail(user.email.toLowerCase());
        // Небольшая задержка чтобы modal успел закрыться перед скроллом
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }

      // Update local user (emailVerified=false)
      setUser(user);
      trackUmamiEvent('signup_success', {
        method: 'email',
        source: getAttributionSource(),
      });
      notify.info('Verification email sent');
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
      // Invalidate duels to refetch with user_vote
      queryClient.invalidateQueries({ queryKey: ['duels'] });
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; code?: string; message?: string };
      if (e.code === 'EMAIL_ALREADY_EXISTS') {
        return notify.error('Email already exists');
      }
      if (e.code === 'USERNAME_ALREADY_EXISTS') {
        return notify.error('Username already taken');
      }
      notify.error(e.message || 'Registration error');
    },
  });
}

export function useVerifyEmail() {
  const queryClient = useQueryClient();
  const setUnverifiedEmail = useAuth((s) => s.setUnverifiedEmail);
  return useMutation({
    mutationFn: verifyEmail,
    onSuccess: () => {
      setUnverifiedEmail(null);
      notify.success('Email verified');
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
      // Invalidate duels to refetch with user_vote
      queryClient.invalidateQueries({ queryKey: ['duels'] });
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: forgotPassword,
    onSuccess: () => {
      notify.info('If the account exists, an email has been sent');
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      notify.success('Password changed successfully');
    },
  });
}

export function useSignOut() {
  const setUser = useAuth((s) => s.setUser);
  const setUnverifiedEmail = useAuth((s) => s.setUnverifiedEmail);
  const clearTokens = useAuth((s) => s.clearTokens);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await signOutApi();
      } finally {
        // Всегда чистим токены и состояние локально, даже если запрос провалился
        clearTokens();
        // Очищаем email address через store
        setUnverifiedEmail(null);

        // Очищаем Zustand и кэш Query немедленно
        setUser(null);
        queryClient.removeQueries({ queryKey: AUTH_QUERY_KEYS.me });
        // Для уверенности очищаем весь кэш, если есть другие закешированные данные пользователя
        queryClient.clear();
      }
    },
    onSuccess: () => {
      notify.success('Signed out');
    },
  });
}

// Set Password (for OAuth users without password)
export function useSetPassword() {
  return useMutation({
    mutationFn: setPassword,
    onSuccess: () => {
      notify.success('Password set successfully');
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; code?: string; message?: string };
      notify.error(e.message || 'Failed to set password');
    },
  });
}

// Get OAuth Providers
const PROVIDERS_QUERY_KEY = ['auth', 'providers'] as const;

export function useProviders() {
  const hasAccessToken = typeof window !== 'undefined' && !!localStorage.getItem(ACCESS_TOKEN_KEY);
  return useQuery({
    queryKey: PROVIDERS_QUERY_KEY,
    queryFn: getProviders,
    enabled: hasAccessToken,
  });
}

// Unlink Provider
export function useUnlinkProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unlinkProvider,
    onSuccess: () => {
      notify.success('Provider unlinked successfully');
      queryClient.invalidateQueries({ queryKey: PROVIDERS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['profile', 'me'] });
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; code?: string; message?: string };
      if (e.message?.includes('last login method')) {
        notify.error('Cannot unlink last login method. Set a password first.');
      } else {
        notify.error(e.message || 'Failed to unlink provider');
      }
    },
  });
}
